/**
 * The trip page's "Text me flight alerts" switch, under the trip's flights
 * (founder, 2026-10-24). It's one setting for the account: on, every flight
 * change email for any of their trips also comes as a text to the number
 * here (server/flight-status-sms.js). Turning it on records the consent with
 * the words shown (src/constants/flightAlertTexts.js); off stops the texts,
 * as a STOP reply does.
 *
 * Renders nothing until texts are switched on (FLIGHT_STATUS_TEXTS).
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MessageSquareText } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { textAlertsApi } from '@/lib/textAlertsApi';
import { FLIGHT_ALERT_TEXTS_DISCLOSURE, FLIGHT_ALERT_TEXTS_LABEL, FLIGHT_ALERT_TEXTS_TERMS_PATH, textablePhone } from '@/constants/flightAlertTexts';

export default function FlightAlertTextsSwitch({ suggestedPhone = '' }) {
  const [state, setState] = useState(null);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    textAlertsApi.get().then((s) => {
      if (!alive) return;
      setState(s);
      setPhone(s?.phone_display || s?.phone || suggestedPhone || '');
    }).catch(() => {});
    return () => { alive = false; };
  }, [suggestedPhone]);

  if (!state?.available) return null;

  const onChange = async (next) => {
    setError(null);
    if (next && !textablePhone(phone)) {
      setError('Enter a mobile number with its area code.');
      return;
    }
    setBusy(true);
    try {
      setState(next ? await textAlertsApi.turnOn({ phone, source: 'trip_page' }) : await textAlertsApi.turnOff());
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
      <div className="flex items-center gap-3">
        <MessageSquareText className="h-4 w-4 flex-shrink-0 text-white/60" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-normal text-white">{FLIGHT_ALERT_TEXTS_LABEL}</p>
          <p className="text-[12px] font-normal leading-snug text-white/45">
            {state.enabled
              ? `On for all your trips, to ${state.phone_display || state.phone}.`
              : 'Delays, schedule changes and cancellations, as texts. For all your trips.'}
          </p>
        </div>
        <Switch
          checked={Boolean(state.enabled)}
          disabled={busy}
          onCheckedChange={onChange}
          aria-label={FLIGHT_ALERT_TEXTS_LABEL}
          className="data-[state=checked]:bg-emerald-500/80 data-[state=unchecked]:bg-white/20"
        />
      </div>
      {!state.enabled && (
        <div className="mt-3 pl-7">
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Mobile number"
            aria-label="Mobile number for flight alert texts"
            className="h-9 max-w-[220px] bg-white/5 border-white/15 text-white text-[14px]"
          />
          <p className="mt-2 text-[11px] font-normal leading-snug text-white/40">
            {FLIGHT_ALERT_TEXTS_DISCLOSURE} See our{' '}
            <a href={FLIGHT_ALERT_TEXTS_TERMS_PATH} className="underline underline-offset-2 hover:text-white/70">Terms</a> and{' '}
            <a href="/privacy" className="underline underline-offset-2 hover:text-white/70">Privacy Policy</a>.
          </p>
        </div>
      )}
      {error && <p className="mt-2 pl-7 text-[12px] font-normal text-brand-300">{error}</p>}
    </div>
  );
}

FlightAlertTextsSwitch.propTypes = {
  suggestedPhone: PropTypes.string,
};
